import { Typography, Box, Button, Link, Alert } from '@mui/material';
import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import styles from '../Auth.module.scss';
import { PASSWORD_MIN_LENGTH } from '../helpers';
import PasswordField from '../components/PasswordField';
import apiReq from 'core/api';

interface FormData {
  password: string;
  confirmPassword: string;
}

interface FormErrors {
  password?: string;
  confirmPassword?: string;
  submit?: string;
}

const ResetPassword = () => {
  const [formData, setFormData] = useState<FormData>({
    password: '',
    confirmPassword: '',
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [tokenError, setTokenError] = useState('');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const tokenFromUrl = useMemo(() => searchParams.get('token'), [searchParams]);

  useEffect(() => {
    const validateToken = async (tokenValue: string) => {
      setTokenError('');
      setIsLoading(true);
      try {
        const response = await apiReq<{ name: string }>(`auth/password/find?token=${tokenValue}`);
        if (response && response.name) {
          setIsLoading(false);
        } else {
          setTokenError('Токен недействителен или истек');
          setIsLoading(false);
        }
      } catch (error) {
        setTokenError('Токен недействителен или истек');
        setIsLoading(false);
      }
    };

    if (!tokenFromUrl) {
      setTokenError('Токен для сброса пароля отсутствует');
      setIsLoading(false);
      return;
    }
    validateToken(tokenFromUrl);
  }, [searchParams, tokenFromUrl]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    if (errors[name as keyof FormErrors]) {
      setErrors((prev) => ({
        ...prev,
        [name]: '',
      }));
    }
  };

  const validateForm = () => {
    const newErrors: FormErrors = {};

    if (!formData.password) {
      newErrors.password = 'Пароль обязателен';
    } else if (formData.password.length < PASSWORD_MIN_LENGTH) {
      newErrors.password = `Пароль должен содержать минимум ${PASSWORD_MIN_LENGTH} символов`;
    }

    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Подтвердите пароль';
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Пароли не совпадают';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (validateForm()) {
      try {
        setIsLoading(true);
        const response = await apiReq<{ status: string }>(`auth/password/set`, 'PATCH', {
          password: formData.password,
          token: tokenFromUrl,
        });
        if (response && response.status) {
          setIsSubmitted(true);
          setIsLoading(false);
        } else {
          setErrors({ submit: 'Ошибка при сбросе пароля. Попробуйте еще раз.' });
          setIsLoading(false);
        }
      } catch (error) {
        setErrors({ submit: 'Ошибка при сбросе пароля. Попробуйте еще раз.' });
        setIsLoading(false);
      }
    }
  };

  const handleBackToLogin = () => {
    navigate('/login');
  };

  const Brand = (
    <Box className={styles['auth-brand']}>
      <img
        className={styles['auth-brand-mark']}
        src={`${process.env.PUBLIC_URL}/logo.svg`}
        alt="Контекст"
        width={26}
        height={26}
      />
      <Box className={styles['auth-brand-name']}>Контекст</Box>
    </Box>
  );

  if (isLoading) {
    return (
      <Box className={styles['auth-container']}>
        <Box className={styles['auth-card']}>
          {Brand}
          <Typography className={styles['auth-title']}>Сброс пароля</Typography>
          <Typography className={styles['auth-subtitle']}>Проверка токена…</Typography>
        </Box>
      </Box>
    );
  }

  if (tokenError) {
    return (
      <Box className={styles['auth-container']}>
        <Box className={styles['auth-card']}>
          {Brand}
          <Typography className={styles['auth-title']}>Ошибка</Typography>
          <Box className={styles['auth-error']}>{tokenError}</Box>
          <Link
            component="button"
            variant="body2"
            onClick={handleBackToLogin}
            className={styles['auth-link']}
          >
            Вернуться к входу
          </Link>
        </Box>
      </Box>
    );
  }

  if (isSubmitted) {
    return (
      <Box className={styles['auth-container']}>
        <Box className={styles['auth-card']}>
          {Brand}
          <Typography className={styles['auth-title']}>Пароль изменён</Typography>
          <Typography className={styles['auth-subtitle']}>
            Теперь вы можете войти в систему с новым паролем.
          </Typography>
          <Alert severity="success" className={styles['auth-message']}>
            Пароль успешно изменён.
          </Alert>
          <Link
            component="button"
            variant="body2"
            onClick={handleBackToLogin}
            className={styles['auth-link']}
          >
            Войти в систему
          </Link>
        </Box>
      </Box>
    );
  }

  return (
    <Box className={styles['auth-container']}>
      <Box className={styles['auth-card']}>
        {Brand}
        <Typography className={styles['auth-title']}>Сброс пароля</Typography>
        <Typography className={styles['auth-subtitle']}>
          Придумайте новый пароль для вашей учётной записи.
        </Typography>

        <Box component="form" onSubmit={handleSubmit} className={styles['auth-form']}>
          <PasswordField
            name="password"
            label="Новый пароль"
            value={formData.password}
            onChange={handleChange}
            error={errors.password}
            helperText={errors.password}
            className={styles['auth-input']}
          />

          <PasswordField
            name="confirmPassword"
            label="Подтвердите пароль"
            value={formData.confirmPassword}
            onChange={handleChange}
            error={errors.confirmPassword}
            helperText={errors.confirmPassword}
            className={styles['auth-input']}
          />

          {errors.submit && <Box className={styles['auth-error']}>{errors.submit}</Box>}

          <Button
            type="submit"
            variant="contained"
            fullWidth
            className={styles['auth-button']}
            disabled={isLoading}
          >
            {isLoading ? 'Сохранение…' : 'Сохранить'}
          </Button>
        </Box>

        <Link
          component="button"
          variant="body2"
          onClick={handleBackToLogin}
          className={styles['auth-link']}
        >
          Вернуться к входу
        </Link>
      </Box>
    </Box>
  );
};

export default ResetPassword;
